/* eslint-disable no-shadow */
/* eslint-disable import/no-unresolved */
/* eslint-disable @typescript-eslint/no-non-null-assertion */

import { LatLngLiteral, LeafletEventHandlerFnMap } from "leaflet";
import React, { useCallback, useEffect, useMemo, useRef } from "react";
import MarkerController from "react-leaflet-enhanced-marker";
import { react, usePrevValue } from "uikit";
import { InputifiedComponentProps } from "uikit/dist/utils/react";

import Language from "../../services/Language";
import useObjectEditor from "../../hooks/useObjectEditor";
import Address from "../../types/Address";
import reveal from "../../utils/reveal";
import NamedMarker from "../NamedMarker";
import Marker from "../Marker";

const EMPTY_ADDRESS: Partial<Address> = {
	country: "",
	countryCode: "",
	settlement: "",
	settlementType: "",
	district: "",
	region: "",
	street: "",
	streetType: "",
	house: "",
	address: "",
};

const PointController = react.inputify<
	PointController.PropsBase,
	PointController.Value
>(
	({
		value,

		autoResolve = true,
		language,
		name,
		editable = true,
		taxiServiceId,

		onChange,

		onClick,
	}) => {
		// eslint-disable-next-line no-param-reassign
		if (!editable) autoResolve = false;

		const currentRevealPromiseRef = useRef<Promise<any> | null>();

		const valueEditor = useObjectEditor(value ?? {}, onChange);

		const coordinates = valueEditor.useGetter("coordinates");
		const setCoordinates = valueEditor.useSetter("coordinates");

		const setAddress = valueEditor.useSetter("address");

		const internalReveal = useCallback(
			async (coordinates: LatLngLiteral) => {
				if (!autoResolve) return;

				setAddress(EMPTY_ADDRESS);

				const revealPromise = reveal(
					coordinates,
					language,
					taxiServiceId,
				);

				currentRevealPromiseRef.current = revealPromise;

				const address = await revealPromise;

				if (currentRevealPromiseRef.current !== revealPromise) return;

				setAddress(address);
			},
			[autoResolve, language, setAddress, taxiServiceId],
		);

		const eventHandlers = useMemo<LeafletEventHandlerFnMap>(
			() => ({
				async dragend(event) {
					const newCoordinates = event.target._latlng;

					setCoordinates({
						lat: newCoordinates.lat,
						lng: newCoordinates.lng,
					});

					internalReveal(newCoordinates);
				},

				// An undefined listener makes Leaflet warn "wrong listener type"
				// on every marker (dev check G5).
				...(onClick ? { click: onClick } : {}),
			}),
			[onClick, internalReveal, setCoordinates],
		);

		const prevCoordinates = usePrevValue(coordinates);

		useEffect(() => {
			if (coordinates && !prevCoordinates) internalReveal(coordinates);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [coordinates, prevCoordinates]);

		useEffect(() => {
			if (coordinates && autoResolve) internalReveal(coordinates);
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [autoResolve]);

		return (
			<>
				{coordinates && (
					<MarkerController
						icon={
							typeof name === "string" ? (
								<NamedMarker key={`markerIcon`} name={name} />
							) : (
								<Marker key={`markerIcon`} />
							)
						}
						draggable={editable}
						position={coordinates}
						eventHandlers={eventHandlers}
					/>
				)}
			</>
		);
	},
);

declare namespace PointController {
	interface Value {
		coordinates?: LatLngLiteral;
		address?: Partial<Address>;
	}

	export interface PropsBase {
		autoResolve?: boolean;
		language: Language;
		name?: string;
		editable?: boolean;
		taxiServiceId?: number;

		onClick?: () => void;
	}

	type Props = PropsBase & InputifiedComponentProps<Value>;
}

export default PointController;
